import { RefObject, useEffect, useState } from 'react'

import { getConfigs, getEnv, ProdEnv, ProdEnvs } from '@top/shared/src/configs'
import { ButtonType, ItemSkipButton } from '@top/shared_deprecated/src/types/Items'
import { Locale } from '@top/shared_deprecated/src/types/Languages'
import {
  IDistBranch,
  IDistRange,
  INumericSelection,
  IScene,
  SceneAction,
  SceneType,
} from '@top/shared_deprecated/src/types/Scene'
import { ISkipButtonStyle } from '@top/shared_deprecated/src/types/Style'

import { debounce } from 'lodash'
import isURL from 'validator/lib/isURL'

export const getBtnWrapperHeights = (
  btnType: ButtonType,
  showSkipButton: boolean,
  sceneType?: SceneType
) => {
  const sceneSpecificHeights: { [key: string]: number } = {
    [SceneType.Numeric]: 88,
    [SceneType.SceneBuilder]: 70,
  }
  const animatedSkipHeight = (sceneType && sceneSpecificHeights[sceneType]) || 48
  const nextOnlySkipHeight = (sceneType && sceneSpecificHeights[sceneType]) || 'auto'

  const nextBtnOnly: { [key: string]: number | string } = {
    [ButtonType.contained]: 68,
    [ButtonType.arrow]: 76,
    [ButtonType.animated]: nextOnlySkipHeight,
  }
  const nextAndSkipBtn: { [key: string]: number | string } = {
    [ButtonType.contained]: 113,
    [ButtonType.arrow]: 122,
    [ButtonType.animated]: animatedSkipHeight,
  }
  return showSkipButton ? nextAndSkipBtn[btnType] : nextBtnOnly[btnType]
}

export const getIframeStyling = (isIframe: boolean) => {
  return isIframe ? { width: '100%', height: '100%' } : {}
}

export const getFromETC = (etc: any, key: string) => {
  return etc && etc[key] ? etc[key] : ''
}

export const handleRedirectUrl = (url: string = '') => {
  if (isURL(url)) {
    setTimeout(() => {
      window.open(url, '_blank')
    }, 400)
  } else {
    throw new Error(`Invalid url ${url}`)
  }
}

export const getCloudinaryUrl = ({
  cloudinaryId,
  zoom,
  cropHeight,
  cropWidth,
  cropX,
  cropY,
  width,
  height,
}: {
  cloudinaryId: string
  zoom?: number
  cropHeight?: number
  cropWidth?: number
  cropX?: number
  cropY?: number
  width?: number
  height?: number
}) => {
  const { CLOUDINARY_URL } = getConfigs()
  if (cloudinaryId === '') return ''
  let url = `${CLOUDINARY_URL}`
  if (zoom) url += `/w_${zoom.toFixed(1)},c_scale`
  if (
    isNumber(cropX) &&
    isNumber(cropY) &&
    isNumber(cropWidth) &&
    isNumber(cropHeight) &&
    (cropHeight as number) > 0 &&
    isNumber(zoom)
  ) {
    url += `/x_${applyZoom(cropX as number, zoom as number)},y_${applyZoom(
      cropY as number,
      zoom as number
    )},w_${applyZoom(cropWidth as number, zoom as number)},h_${applyZoom(
      cropHeight as number,
      zoom as number
    )},c_crop`
  }

  // On distributor, if we don't pass an explicit width we should assume it should fill screen
  url +=
    width && height ? `/w_${width},h_${height}` : `/w_${window.innerWidth},h_${window.innerHeight}`
  url += `,c_fit/${cloudinaryId}`
  return url
}

function applyZoom(value: number, zoom: number) {
  return Math.round(value * zoom)
}

function isNumber(x: number | undefined) {
  return typeof x === 'number'
}

/**
 * @deprecated
 */
export const isDevEnvironment = (): boolean => {
  const env = getEnv()
  return process.env.NODE_ENV === 'development' || !ProdEnvs.includes(env as ProdEnv)
}

export function alphaToHexValue(color: string, alphaValue?: number) {
  const hex = color.charAt(0) === '#' ? color.substring(1, 7) : color
  let alpha = alphaValue && Math.round(alphaValue * 255).toString(16)
  if (!alpha || (alpha && alpha.length === 1)) {
    alpha = `0${alpha}`
  }
  return `#${hex}${alpha}`
}

// Returns as value out of 100
export function getOpacityValue(color: string) {
  // If no opacity value in hex, return 100
  if (color && color.length && color.length < 8) {
    return 100
  }
  const hex = color.charAt(0) === '#' ? color.substring(1, 9) : color
  const alphaValue = hex.substring(6, 8)
  const opacity = (parseInt(alphaValue, 16) / 255) * 100
  return Math.round(opacity)
}

export const useIsWindowResizing = (ref: RefObject<HTMLDivElement>): boolean => {
  const [isResizing, setIsResizing] = useState<boolean>(false)
  useEffect(() => {
    const setResizingToFalse = debounce<() => void>(() => {
      setIsResizing(false)
    }, 100)
    const handleResize = () => {
      if (ref.current) {
        setIsResizing(true)
        setResizingToFalse()
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      setResizingToFalse.cancel()
    }
  }, [ref])
  return isResizing
}

export const getBranch = (branches: Array<IDistBranch>, selId: string): IDistBranch | undefined => {
  return branches.find((b) => b.required_selections[selId] !== undefined)
}

export type IResolvedAction = {
  action: SceneAction
  nextSceneUuid?: string
}

/**
 * Resolve where an *already-answered* scene leads, given the stored selection id.
 * Mirrors the per-scene branch logic — numeric scenes branch on the selected value
 * via `range_groupings`, choice scenes via `branches` (`required_selections`) — and
 * falls back to the scene-level action. Used to replay a resuming visitor's path.
 *
 * Note: `selfResults` persists one selection id per scene (`ISelfResults`), so both
 * single- and multi-select choice scenes are replayed the same way — `getBranch` matches
 * the first branch whose `required_selections` contains that id. This mirrors runtime
 * exactly for single-select; for multi-select (runtime matches the full set via
 * `getSelectedBranch`) it is a best-effort match on the one stored id, which may differ
 * when the matched branch requires additional selections.
 */
export const resolveNextAction = (scene: IScene, selectionId: string): IResolvedAction => {
  const { branches, range_groupings } = scene.etc
  const sceneFallback: IResolvedAction = {
    action: scene.action_type,
    nextSceneUuid: scene.action_value,
  }

  // Numeric scenes branch on the selected value.
  if (range_groupings && range_groupings.length) {
    const selection = scene.selections?.find((s) => s.id === selectionId) as
      | INumericSelection
      | undefined
    const value = selection?.etc?.value
    if (value === undefined) {
      return sceneFallback
    }
    for (const range of range_groupings as IDistRange[]) {
      if (range.action_type && value >= range.start && value <= range.end) {
        return { action: range.action_type, nextSceneUuid: range.action_value }
      }
    }
    return sceneFallback
  }

  // Choice scenes branch on the matched selection.
  if (branches && branches.length) {
    const branch = getBranch(branches, selectionId)
    if (branch) {
      return { action: branch.action_type, nextSceneUuid: branch.action_value }
    }
  }

  return sceneFallback
}

/**
 * Replay a returning visitor's answered path (`selfResults`) from the first scene,
 * following each answered scene's branch/action, and return the index of the first
 * *unanswered* scene to resume on. Returns `null` when there is nothing to resume to
 * (no progress, or the path already ended via END_ACTIVITY/REDIRECT or ran to the end).
 */
export const computeResumeSceneIndex = (
  scenes: Array<IScene>,
  scenesMap: { [uuid: string]: number },
  selfResults: { [sceneId: string]: string }
): number | null => {
  if (!scenes.length || !Object.keys(selfResults).length) {
    return null
  }

  let scene: IScene | undefined = scenes[0]
  const visited = new Set<string>()

  while (scene && !visited.has(scene.uuid)) {
    visited.add(scene.uuid)

    const selectionId = selfResults[scene.id]
    if (!selectionId) {
      // First unanswered scene — resume here (only if it's past the start).
      const index = scenesMap[scene.uuid]
      return index > 0 ? index : null
    }

    const { action, nextSceneUuid } = resolveNextAction(scene, selectionId)
    if (action !== SceneAction.NEXT_SCENE) {
      return null // path ended (END_ACTIVITY / REDIRECT)
    }

    scene =
      nextSceneUuid && scenesMap[nextSceneUuid] !== undefined
        ? scenes[scenesMap[nextSceneUuid]]
        : scenes[scenesMap[scene.uuid] + 1] // no explicit target → natural order
  }

  return null
}

export const getSelectedBranch = (branches: Array<IDistBranch>, selected: Array<string>) => {
  for (const branch of branches) {
    let match = false
    const requiredSelections = branch.required_selections
    if (requiredSelections && Object.keys(requiredSelections).length === selected.length) {
      match = true
      for (const selection of selected) {
        if (!branch.required_selections[selection]) {
          match = false
        }
      }
    }
    if (match) {
      return branch
    }
  }
  return null
}

export const getSkipButtonData = (
  ITEM_SKIP_BUTTON: ItemSkipButton | undefined,
  locale?: Locale
): Partial<{
  skip_text: string
  skip_style: ISkipButtonStyle
}> => {
  if (ITEM_SKIP_BUTTON === undefined) {
    return { skip_text: undefined, skip_style: undefined }
  }

  const {
    etc: { text: skip_text, translations },
    style: skip_style,
  } = ITEM_SKIP_BUTTON as ItemSkipButton

  // Use text from etc text property for legacy scenes
  return { skip_text: (locale && translations?.[locale]) ?? skip_text, skip_style }
}
